import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const sectionLinks = [
    { name: 'Home', id: 'home', link: '/'},
    { name: 'What We Do', id: 'services', link: ''},
    { name: 'How We Work', id: 'process', link: ''},
    { name: 'Our Work', id: 'work', link: '/ourwork' },
    { name: 'About', id: 'about', link: '/about' },
    { name: 'Careers', id: 'careers', link: '/careers' },
  ];

  const scrollToSection = (id) => {
    const scrollAction = () => {
      const section = document.getElementById(id);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    };

    if (location.pathname === '/') {
      scrollAction();
      return;
    }

    navigate('/');
    setTimeout(scrollAction, 120);
  };

  return (
    <header
      data-testid="main-header"
      className="fixed top-0 left-0 right-0 z-50 border-b border-slate-200/80 bg-white/95 backdrop-blur-xl"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-[66px]">
          {/* Logo */}
          <Link
            to="/"
            data-testid="header-logo"
            className="flex items-center space-x-2"
          >
            <span className="text-xl lg:text-2xl font-bold text-slate-950 tracking-tight">
              Build <span className="text-indigo-600">&</span> Hire
            </span>
          </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-0.5">
                {sectionLinks.map((link) => {

                    if (link.link) {
                        return (
                            <Link
                                key={link.id}
                                to={link.link}
                                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                                className="rounded-md px-3.5 py-1.5 text-sm font-medium text-slate-500 transition-colors  hover:text-slate-900"
                            >
                                {link.name}
                            </Link>
                        );
                    } else {
                        return (
                            <button
                                key={link.id}
                                type="button"
                                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                                onClick={() => scrollToSection(link.id)}
                                className="rounded-md px-3.5 py-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
                            >
                                {link.name}
                            </button>
                        )
                    }  
                })}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center">
                <a
                    href="mailto:info@buildupriseup.com?subject=Technical%20Consultation%20Request"
                    className="inline-flex h-9 items-center justify-center rounded-md bg-slate-950 px-5 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
                >
                    Book a Free Technical Call
                </a>
            </div>

            {/* Mobile Menu Button */}
            <button
                data-testid="mobile-menu-toggle"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-slate-600 hover:text-slate-950"
            >
                {isMobileMenuOpen ? (
                <X className="h-6 w-6" strokeWidth={1.5} />
                ) : (
                <Menu className="h-6 w-6" strokeWidth={1.5} />
                )}
            </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
            <div
                data-testid="mobile-menu"
                className="lg:hidden mobile-menu-enter border-t border-slate-200/70 bg-white/95 backdrop-blur-md py-4 shadow-lg"
            >
            <div className="space-y-1">
                {sectionLinks.map((link) => {

                    if (link.link) {
                        return (
                            <Link
                                key={link.id}
                                to={link.link}
                                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                                className="block rounded-xl px-4 py-2 text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-950"
                            >
                                {link.name}
                            </Link>
                        );
                    } else {
                        return (
                            <button
                                key={link.id}
                                type="button"
                                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                                onClick={() => scrollToSection(link.id)}
                                className="block w-full rounded-xl px-4 py-2 text-left text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-950"
                            >
                                {link.name}
                            </button>
                        )
                    }  
                })}
              
              <div className="pt-4 px-4">
                <a
                  href="mailto:info@buildupriseup.com?subject=Technical%20Consultation%20Request"
                  className="inline-flex h-10 w-full items-center justify-center rounded-md bg-slate-950 px-4 text-sm font-medium text-white transition-colors hover:bg-slate-800"
                >
                  Book a Free Technical Call
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
